<script setup lang="ts">
  import { reactiveOmit } from '@vueuse/core'
  import type { VariantProps } from 'class-variance-authority'
  import {
    ToggleGroupRoot,
    type ToggleGroupRootEmits,
    type ToggleGroupRootProps,
    useForwardPropsEmits
  } from 'reka-ui'
  import { type HTMLAttributes, provide } from 'vue'
  import { cn } from '../../../lib/utils'
  import type { toggleVariants } from '../toggle'

  type ToggleGroupVariants = VariantProps<typeof toggleVariants>

  const props = defineProps<
    ToggleGroupRootProps & {
      class?: HTMLAttributes['class']
      variant?: ToggleGroupVariants['variant']
      size?: ToggleGroupVariants['size']
      separated?: boolean
    }
  >()
  const emits = defineEmits<ToggleGroupRootEmits>()

  provide('toggleGroup', {
    variant: props.variant,
    size: props.size,
    separated: props.separated
  })

  const delegatedProps = reactiveOmit(props, 'class', 'size', 'variant')
  const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <ToggleGroupRoot
    data-slot="toggle-group"
    :data-size="size"
    :data-variant="variant"
    v-bind="forwarded"
    :class="
      cn(
        'group/toggle-group flex w-fit items-center',
        separated ? 'gap-1.5' : 'data-[variant=outline]:shadow-xs rounded-md',
        props.class
      )
    "
  >
    <slot />
  </ToggleGroupRoot>
</template>
